/**
 * Life4Earth V2 — Schedule Series Points API Adapter
 *
 * Replaces: localStorage keys l4e_progress_series_v2, l4e_schedule_progress_series_v1
 *           from Data/ScheduleProgress/scheduleProgressOverrides.ts
 * Endpoint: /api/ScheduleSeriesPoints
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface ScheduleSeriesPointResponse {
  id: string;
  projectId: string;
  instanceGuid: string | null;
  reportingDate: string; // "YYYY-MM-DD"
  baselineComplete: number | null;
  actualComplete: number | null;
  forecastComplete: number | null;
  createdAt: string;
}

export interface CreateScheduleSeriesPointRequest {
  projectId: string;
  instanceGuid?: string;
  reportingDate: string;
  baselineComplete?: number;
  actualComplete?: number;
  forecastComplete?: number;
}

// ── API Functions ────────────────────────────────────────────

/**
 * Fetch ALL schedule series points across all projects.
 * Used by App.tsx to build the progressByProject lookup.
 * Results ordered by project_id ASC, reporting_date ASC.
 */
export async function fetchAllScheduleSeriesPoints(): Promise<
  ScheduleSeriesPointResponse[]
> {
  return apiFetch<ScheduleSeriesPointResponse[]>(
    "/api/ScheduleSeriesPoints/all"
  );
}

/**
 * Convert API response rows into the seed-data shape that App.tsx
 * and the kpi-engine expect (project_GUID, reporting_date, etc.).
 *
 * DEV-851: `created_at` preserved so App.tsx can build the data-reliability
 * engine's `progress_entries` input — the engine needs both reporting_date
 * (period the entry covers) and created_at (when it was actually submitted)
 * to score Timeliness. Existing thermometer / scoring consumers ignore the
 * field; it's a permissive addition.
 */
export function schedulePointToSeedShape(
  p: ScheduleSeriesPointResponse
): Record<string, any> {
  return {
    project_GUID: normalizeGuid(p.projectId),
    instance_guid: normalizeGuid(p.instanceGuid),
    reporting_date: p.reportingDate,
    baseline_complete: p.baselineComplete ?? null,
    actual_complete: p.actualComplete ?? null,
    forecast_complete: p.forecastComplete ?? null,
    created_at: p.createdAt,
  };
}

/**
 * Fetch schedule series points for a project+instance.
 * Results ordered by reporting_date DESC (most recent first).
 *
 * Use limit=2 for the KpiGridTable's "last two reporting dates" pattern (§8.5).
 */
export async function fetchScheduleSeriesPoints(
  projectId: string,
  instanceGuid?: string,
  limit?: number
): Promise<ScheduleSeriesPointResponse[]> {
  const params = new URLSearchParams({ projectId });
  if (instanceGuid) params.set("instanceGuid", instanceGuid);
  if (limit) params.set("limit", limit.toString());

  return apiFetch<ScheduleSeriesPointResponse[]>(
    `/api/ScheduleSeriesPoints?${params.toString()}`
  );
}

export async function createScheduleSeriesPoint(
  request: CreateScheduleSeriesPointRequest
): Promise<ScheduleSeriesPointResponse> {
  return apiFetch<ScheduleSeriesPointResponse>("/api/ScheduleSeriesPoints", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Batch upsert: insert or update multiple points at once.
 * Matched on (project_id, instance_guid, reporting_date).
 * Max 500 points per batch.
 */
export async function batchUpsertScheduleSeriesPoints(
  points: CreateScheduleSeriesPointRequest[]
): Promise<ScheduleSeriesPointResponse[]> {
  return apiFetch<ScheduleSeriesPointResponse[]>(
    "/api/ScheduleSeriesPoints/batch",
    {
      method: "POST",
      body: JSON.stringify({ points }),
    }
  );
}

export async function deleteScheduleSeriesPoint(
  id: string
): Promise<void> {
  return apiFetch<void>(`/api/ScheduleSeriesPoints/${id}`, {
    method: "DELETE",
  });
}

/**
 * Delete schedule series points by date range.
 * Removes all points for the given project+instance that fall outside
 * the specified start/end window.
 * Used by the Apply handler to clean up stale records after a date-range change.
 */
export async function deleteScheduleSeriesPointsByDateRange(
  projectId: string,
  instanceGuid: string | undefined,
  keepStartDate: string,
  keepEndDate: string,
  options?: { softDelete?: boolean },
): Promise<{ deletedCount: number }> {
  return apiFetch<{ deletedCount: number }>(
    "/api/ScheduleSeriesPoints/cleanup-range",
    {
      method: "POST",
      body: JSON.stringify({
        projectId,
        instanceGuid: instanceGuid || null,
        keepStartDate,
        keepEndDate,
        softDelete: !!options?.softDelete,
      }),
    }
  );
}
